/**
 * Lecture Mapper - Backend to Frontend
 * @description Maps backend lecture data (shortened fields) to frontend lecture data (full fields)
 */

import { BackendLecture } from '@/types/backend.types';
import { LectureResponse, LectureEventType } from '@/types/lecture.types';

export class LectureMapper {
  /**
   * Map single backend lecture to frontend lecture
   */
  static toFrontend(backendLecture: BackendLecture): LectureResponse {
    return {
      id: backendLecture.id,
      skey: backendLecture.skey || '', // Handle missing skey in response
      devId: backendLecture.devId,
      opId: backendLecture.opId,
      ts: backendLecture.ts,
      tz: backendLecture.tz,
      coords: {
        lat: backendLecture.coords.lat,
        long: backendLecture.coords.long,
      },
      type: backendLecture.type as LectureEventType,
      ang: {
        lat: backendLecture.ang.lat,
        long: backendLecture.ang.long,
      },
      lim: {
        lat: backendLecture.lim.lat,
        long: backendLecture.lim.long,
      },
      seq: backendLecture.seq,
      order: backendLecture.order,
      createdAt: backendLecture.createdAt,
      updatedAt: backendLecture.updatedAt,
    };
  }

  /**
   * Map array of backend lectures to frontend lectures
   */
  static toFrontendArray(backendLectures: BackendLecture[]): LectureResponse[] {
    return backendLectures.map((lecture) => LectureMapper.toFrontend(lecture));
  }

  /**
   * Map frontend lecture to backend lecture (for create/update operations)
   */
  static toBackend(frontendLecture: Partial<LectureResponse>): Partial<BackendLecture> {
    const backendLecture: Partial<BackendLecture> = {};

    if (frontendLecture.id !== undefined) backendLecture.id = frontendLecture.id;
    if (frontendLecture.skey !== undefined) backendLecture.skey = frontendLecture.skey;
    if (frontendLecture.opId !== undefined) backendLecture.opId = frontendLecture.opId;
    if (frontendLecture.ts !== undefined) backendLecture.ts = frontendLecture.ts;
    if (frontendLecture.tz !== undefined) backendLecture.tz = frontendLecture.tz;
    if (frontendLecture.type !== undefined) backendLecture.type = frontendLecture.type;
    if (frontendLecture.seq !== undefined) backendLecture.seq = frontendLecture.seq;
    if (frontendLecture.order !== undefined) backendLecture.order = frontendLecture.order;
    if (frontendLecture.createdAt !== undefined)
      backendLecture.createdAt = frontendLecture.createdAt;
    if (frontendLecture.updatedAt !== undefined)
      backendLecture.updatedAt = frontendLecture.updatedAt;

    // Handle coordinates
    if (frontendLecture.coords) {
      backendLecture.coords = {
        lat: frontendLecture.coords.lat,
        long: frontendLecture.coords.long,
      };
    }

    // Handle angles
    if (frontendLecture.ang) {
      backendLecture.ang = {
        lat: frontendLecture.ang.lat,
        long: frontendLecture.ang.long,
      };
    }

    // Handle angle limits
    if (frontendLecture.lim) {
      backendLecture.lim = {
        lat: frontendLecture.lim.lat,
        long: frontendLecture.lim.long,
      };
    }

    return backendLecture;
  }

  /**
   * Map array of frontend lectures to backend lectures
   */
  static toBackendArray(frontendLectures: Partial<LectureResponse>[]): Partial<BackendLecture>[] {
    return frontendLectures.map((lecture) => LectureMapper.toBackend(lecture));
  }
}
